/* eslint-disable no-redeclare */
import { Reduce } from "./Reduce"

/**
 * Composes multiple functions into a single function.
 * Functions are applied left-to-right (like pipe, not traditional compose).
 * Type-safe with overloads for up to 9 functions.
 *
 * @param fns - Functions to compose
 * @returns Composed function that applies all functions in sequence
 *
 * @example
 * ```ts
 * import { Composition } from "@pdfking/tfunc"
 *
 * const addOne = (x: number) => x + 1
 * const double = (x: number) => x * 2
 * const toString = (x: number) => `Result: ${x}`
 *
 * const process = Composition(addOne, double, toString)
 * process(5) // "Result: 12" (5 + 1 = 6, 6 * 2 = 12)
 * ```
 */

// Function overloads for type-safe composition (following fp-ts pattern)
export function Composition<A, B>(f1: (a: A) => B): (a: A) => B
export function Composition<A, B, C>(
  f1: (a: A) => B,
  f2: (b: B) => C
): (a: A) => C
export function Composition<A, B, C, D>(
  f1: (a: A) => B,
  f2: (b: B) => C,
  f3: (c: C) => D
): (a: A) => D
export function Composition<A, B, C, D, E>(
  f1: (a: A) => B,
  f2: (b: B) => C,
  f3: (c: C) => D,
  f4: (d: D) => E
): (a: A) => E
export function Composition<A, B, C, D, E, F>(
  f1: (a: A) => B,
  f2: (b: B) => C,
  f3: (c: C) => D,
  f4: (d: D) => E,
  f5: (e: E) => F
): (a: A) => F
export function Composition<A, B, C, D, E, F, G>(
  f1: (a: A) => B,
  f2: (b: B) => C,
  f3: (c: C) => D,
  f4: (d: D) => E,
  f5: (e: E) => F,
  f6: (f: F) => G
): (a: A) => G
export function Composition<A, B, C, D, E, F, G, H>(
  f1: (a: A) => B,
  f2: (b: B) => C,
  f3: (c: C) => D,
  f4: (d: D) => E,
  f5: (e: E) => F,
  f6: (f: F) => G,
  f7: (g: G) => H
): (a: A) => H
export function Composition<A, B, C, D, E, F, G, H, I>(
  f1: (a: A) => B,
  f2: (b: B) => C,
  f3: (c: C) => D,
  f4: (d: D) => E,
  f5: (e: E) => F,
  f6: (f: F) => G,
  f7: (g: G) => H,
  f8: (h: H) => I
): (a: A) => I
export function Composition<A, B, C, D, E, F, G, H, I, J>(
  f1: (a: A) => B,
  f2: (b: B) => C,
  f3: (c: C) => D,
  f4: (d: D) => E,
  f5: (e: E) => F,
  f6: (f: F) => G,
  f7: (g: G) => H,
  f8: (h: H) => I,
  f9: (i: I) => J
): (a: A) => J
// Implementation
export function Composition(
  ...fns: Array<(arg: any) => any>
): (arg: any) => any {
  return (input: any) => Reduce((v, f) => f(v), fns, input)
}
